import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_DRAWER_FRAGMENT_SECTION_ID = 'cart-drawer-fragment';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {Set<string>} */
  #cartDrawerOptimisticPendingTokens = new Set();

  /** @type {number} */
  #cartDrawerLatestOptimisticItemCount = 0;

  /** @type {number} */
  #cartDrawerLatestOptimisticFinalSequence = 0;

  /** @type {number} */
  #cartDrawerLatestCountUpdateSequence = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const cartItemsHtml = parsedResponseText.sections[this.sectionId];
        const newSectionHTML = new DOMParser().parseFromString(cartItemsHtml, 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        this.#morphCartItemsSection(cartItemsHtml, newSectionHTML);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const eventData = event.detail.data || {};

    if (eventData.cartCountUpdateOnly) {
      this.#handleCartDrawerCountUpdate(eventData);
      this.#handleCartDrawerOptimisticLineFeedback(eventData);
      return;
    }

    if (eventData.lightweightSectionRefreshPending && eventData.cartDrawerOptimisticPending) {
      this.#handleCartDrawerOptimisticPending(eventData);
      return;
    }

    if (eventData.lightweightSectionRefreshPending || eventData.cartDrawerMirrorRefreshOnly) return;

    const shouldApplyFinal = this.#handleCartDrawerOptimisticFinal(eventData);
    if (!shouldApplyFinal) return;

    if (eventData.cartDrawerOptimisticFailed) {
      this.#removeCartDrawerOptimisticLineItems(eventData);
      this.#syncCartDrawerOptimisticPendingState();
      return;
    }

    const cartDrawerFragmentHtml = eventData.sections?.[CART_DRAWER_FRAGMENT_SECTION_ID];
    if (cartDrawerFragmentHtml && this.#morphCartDrawerFragment(cartDrawerFragmentHtml)) {
      this.#updateCartQuantitySelectorButtonStates();
      this.#syncCartDrawerOptimisticPendingState();
      return;
    }

    const cartItemsHtml = eventData.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphCartItemsSection(cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
      this.#syncCartDrawerOptimisticPendingState();
    } else {
      const renderPromise = sectionRenderer.renderSection(this.sectionId, { cache: false });

      if (eventData.cartDrawerOptimisticToken) {
        renderPromise.finally(() => this.#syncCartDrawerOptimisticPendingState());
      }
    }
  };

  /**
   * Shows an inert pending shell in the cart drawer and updates visible drawer count optimistically.
   * @param {Record<string, any>} eventData
   */
  #handleCartDrawerOptimisticPending(eventData) {
    const dialog = this.#cartDrawerDialog();
    if (!dialog) return;

    const token = eventData.cartDrawerOptimisticToken;
    const shouldPreserveContent = eventData.cartDrawerPreserveContentPending === true;
    const optimisticItemCount = this.#normalizeCartItemCount(
      eventData.optimisticItemCount,
      this.#readCartDrawerItemCount()
    );

    if (token && !shouldPreserveContent) this.#cartDrawerOptimisticPendingTokens.add(token);

    this.#cartDrawerLatestOptimisticItemCount = optimisticItemCount;
    this.#updateCartDrawerVisibleCount(optimisticItemCount);

    if (shouldPreserveContent) {
      this.#handleCartDrawerOptimisticLineFeedback(eventData);
      this.#setCartDrawerPendingState(false);
      return;
    }

    this.#setCartDrawerPendingState(true);
  }

  /**
   * Tracks final optimistic responses and rejects older final fragments that arrive late.
   * @param {Record<string, any>} eventData
   * @returns {boolean}
   */
  #handleCartDrawerOptimisticFinal(eventData) {
    const token = eventData.cartDrawerOptimisticToken;
    if (!token) return true;

    const sequence = this.#normalizeCartItemCount(eventData.cartDrawerOptimisticSequence, 0);

    this.#cartDrawerOptimisticPendingTokens.delete(token);

    if (sequence && sequence < this.#cartDrawerLatestOptimisticFinalSequence) {
      this.#syncCartDrawerOptimisticPendingState();
      return false;
    }

    if (sequence) this.#cartDrawerLatestOptimisticFinalSequence = sequence;

    return true;
  }

  /**
   * Updates only drawer count bubbles from a minimal count event.
   * @param {Record<string, any>} eventData
   */
  #handleCartDrawerCountUpdate(eventData) {
    if (!this.#cartDrawerDialog()) return;

    const sequence = this.#normalizeCartItemCount(eventData.cartDrawerOptimisticSequence, 0);

    if (sequence && sequence < this.#cartDrawerLatestCountUpdateSequence) return;
    if (sequence) this.#cartDrawerLatestCountUpdateSequence = sequence;

    const countValue = eventData.cartDrawerOptimisticPending
      ? eventData.optimisticItemCount
      : eventData.itemCount ?? eventData.optimisticItemCount;
    const itemCount = this.#normalizeCartItemCount(countValue, this.#readCartDrawerItemCount());

    this.#updateCartDrawerVisibleCount(itemCount);
  }

  /**
   * Shows or removes an optimistic cart row for cart drawer upsell fast-path events.
   * @param {Record<string, any>} eventData
   */
  #handleCartDrawerOptimisticLineFeedback(eventData) {
    if (eventData.cartDrawerUpsellFastPath !== true) return;

    if (eventData.cartDrawerOptimisticFailed) {
      this.#removeCartDrawerOptimisticLineItems(eventData);
      return;
    }

    if (eventData.cartDrawerOptimisticPending) {
      this.#renderCartDrawerOptimisticLineItem(eventData);
    }
  }

  /**
   * Renders an inert optimistic row inside the existing drawer cart table.
   * @param {Record<string, any>} eventData
   */
  #renderCartDrawerOptimisticLineItem(eventData) {
    const lineItem = eventData.optimisticLineItem;
    if (!lineItem || typeof lineItem !== 'object') return;

    const variantId = String(lineItem.variantId || '');
    if (!variantId) return;

    const tableBody = this.querySelector('.cart-items__table tbody');
    if (!(tableBody instanceof HTMLTableSectionElement)) return;

    const quantity = Math.max(1, this.#normalizeCartItemCount(lineItem.quantity, 1));
    const sequence = this.#normalizeCartItemCount(eventData.cartDrawerOptimisticSequence, 0);
    const existingRow = this.#findCartDrawerOptimisticLineItem(variantId);

    if (existingRow) {
      const lastSequence = this.#normalizeCartItemCount(
        existingRow.dataset.cartDrawerOptimisticLineLastSequence,
        0
      );

      if (sequence && sequence <= lastSequence) return;

      const nextQuantity =
        this.#normalizeCartItemCount(existingRow.dataset.cartDrawerOptimisticLineQuantity, 0) + quantity;
      this.#updateCartDrawerOptimisticLineQuantity(existingRow, nextQuantity, sequence);
      this.#markCartDrawerUpsellFastPath('optimistic-line-render', {
        quantity: nextQuantity,
        updatedExisting: true,
        variantId,
      });
      return;
    }

    const row = document.createElement('tr');
    row.className = 'cart-items__table-row cart-items__table-row--optimistic';
    row.setAttribute('role', 'row');
    row.dataset.cartDrawerOptimisticLine = 'true';
    row.dataset.cartDrawerOptimisticLineVariantId = variantId;
    row.dataset.cartDrawerOptimisticLineToken = String(eventData.cartDrawerOptimisticToken || '');
    row.dataset.cartDrawerOptimisticLineQuantity = String(quantity);
    row.dataset.cartDrawerOptimisticLineLastSequence = String(sequence);

    row.append(
      this.#createCartDrawerOptimisticMediaCell(lineItem),
      this.#createCartDrawerOptimisticDetailsCell(lineItem),
      this.#createCartDrawerOptimisticQuantityCell(quantity),
      this.#createCartDrawerOptimisticPriceCell(lineItem)
    );

    tableBody.insertBefore(row, tableBody.firstElementChild);

    this.#markCartDrawerUpsellFastPath('optimistic-line-render', {
      quantity,
      updatedExisting: false,
      variantId,
    });
  }

  /**
   * Creates the optimistic row media cell.
   * @param {Record<string, any>} lineItem
   * @returns {HTMLTableCellElement}
   */
  #createCartDrawerOptimisticMediaCell(lineItem) {
    const cell = document.createElement('td');
    cell.className = 'cart-items__media';
    cell.setAttribute('role', 'cell');
    cell.setAttribute('headers', 'productImage');

    const imageSrc = String(lineItem.imageSrc || '');
    if (!imageSrc) return cell;

    const media = document.createElement(lineItem.url ? 'a' : 'span');
    media.className = 'cart-items__media-container';
    media.setAttribute('style', '--ratio:1;');

    if (media instanceof HTMLAnchorElement) {
      media.href = String(lineItem.url || '#');
    }

    const image = document.createElement('img');
    image.className = 'cart-items__media-image border-style';
    image.src = imageSrc;
    image.alt = String(lineItem.imageAlt || lineItem.title || '');
    image.loading = 'eager';

    media.append(image);
    cell.append(media);

    return cell;
  }

  /**
   * Creates the optimistic row details cell.
   * @param {Record<string, any>} lineItem
   * @returns {HTMLTableCellElement}
   */
  #createCartDrawerOptimisticDetailsCell(lineItem) {
    const cell = document.createElement('td');
    cell.className = 'cart-items__details cart-primary-typography';
    cell.setAttribute('role', 'cell');
    cell.setAttribute('headers', 'productInformation');

    const titleWrapper = document.createElement('p');
    const title = document.createElement(lineItem.url ? 'a' : 'span');
    title.className = 'cart-items__title';

    if (title instanceof HTMLAnchorElement) {
      title.href = String(lineItem.url || '#');
    }

    title.textContent = String(lineItem.title || '');
    titleWrapper.append(title);

    const status = document.createElement('p');
    status.className = 'cart-items__optimistic-status cart-secondary-typography';
    status.textContent = String(lineItem.statusText || 'Adding...');

    cell.append(titleWrapper, status);

    return cell;
  }

  /**
   * Creates the optimistic row quantity cell.
   * @param {number} quantity
   * @returns {HTMLTableCellElement}
   */
  #createCartDrawerOptimisticQuantityCell(quantity) {
    const cell = document.createElement('td');
    cell.className = 'cart-items__quantity cart-primary-typography';
    cell.setAttribute('role', 'cell');
    cell.setAttribute('headers', 'quantity');

    const quantityLabel = this.querySelector('#quantity')?.textContent?.trim() || 'Quantity';
    const quantityText = document.createElement('p');
    quantityText.className = 'cart-items__optimistic-quantity';
    quantityText.dataset.cartDrawerOptimisticQuantityText = 'true';
    quantityText.textContent = `${quantityLabel}: ${quantity}`;

    cell.append(quantityText);

    return cell;
  }

  /**
   * Creates the optimistic row price cell.
   * @param {Record<string, any>} lineItem
   * @returns {HTMLTableCellElement}
   */
  #createCartDrawerOptimisticPriceCell(lineItem) {
    const cell = document.createElement('td');
    cell.className = 'cart-items__price cart-secondary-typography';
    cell.setAttribute('role', 'cell');
    cell.setAttribute('headers', 'productTotal');
    cell.textContent = String(lineItem.priceText || '');

    return cell;
  }

  /**
   * Updates the visible quantity text on an existing optimistic row.
   * @param {HTMLTableRowElement} row
   * @param {number} quantity
   * @param {number} sequence
   */
  #updateCartDrawerOptimisticLineQuantity(row, quantity, sequence) {
    row.dataset.cartDrawerOptimisticLineQuantity = String(quantity);
    row.dataset.cartDrawerOptimisticLineLastSequence = String(sequence);

    const quantityLabel = this.querySelector('#quantity')?.textContent?.trim() || 'Quantity';
    const quantityText = row.querySelector('[data-cart-drawer-optimistic-quantity-text]');
    if (quantityText) quantityText.textContent = `${quantityLabel}: ${quantity}`;
  }

  /**
   * Removes optimistic rows for a failed add or failed reconciliation.
   * @param {Record<string, any>} eventData
   */
  #removeCartDrawerOptimisticLineItems(eventData) {
    const variantId = String(eventData.optimisticLineItem?.variantId || eventData.variantId || '');
    const token = String(eventData.cartDrawerOptimisticToken || '');
    const selector = variantId
      ? `[data-cart-drawer-optimistic-line][data-cart-drawer-optimistic-line-variant-id="${this.#escapeSelectorValue(
          variantId
        )}"]`
      : token
        ? `[data-cart-drawer-optimistic-line][data-cart-drawer-optimistic-line-token="${this.#escapeSelectorValue(
            token
          )}"]`
        : '[data-cart-drawer-optimistic-line]';

    this.querySelectorAll(selector).forEach((row) => row.remove());
  }

  /**
   * Finds an optimistic row by variant ID.
   * @param {string} variantId
   * @returns {HTMLTableRowElement | null}
   */
  #findCartDrawerOptimisticLineItem(variantId) {
    const row = this.querySelector(
      `[data-cart-drawer-optimistic-line][data-cart-drawer-optimistic-line-variant-id="${this.#escapeSelectorValue(
        variantId
      )}"]`
    );

    return row instanceof HTMLTableRowElement ? row : null;
  }

  /**
   * Escapes an attribute selector value.
   * @param {string} value
   * @returns {string}
   */
  #escapeSelectorValue(value) {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /**
   * Marks cart drawer upsell perceived-feedback timing.
   * @param {string} step
   * @param {Record<string, string | number | boolean>} detail
   */
  #markCartDrawerUpsellFastPath(step, detail = {}) {
    const perf = globalThis.performance;
    if (!perf?.mark) return;

    try {
      perf.mark(`cart-performance:cart-drawer-upsell-fast-add:${step}`, { detail });
    } catch (error) {
      perf.mark(`cart-performance:cart-drawer-upsell-fast-add:${step}`);
    }
  }

  /**
   * Keeps the pending shell visible while any newer optimistic add has not reconciled.
   */
  #syncCartDrawerOptimisticPendingState() {
    if (this.#cartDrawerOptimisticPendingTokens.size > 0) {
      this.#setCartDrawerPendingState(true);
      this.#updateCartDrawerVisibleCount(this.#cartDrawerLatestOptimisticItemCount);
      return;
    }

    this.#setCartDrawerPendingState(false);
  }

  /**
   * Toggles the cart drawer pending shell without announcing duplicate status messages.
   * @param {boolean} isPending
   */
  #setCartDrawerPendingState(isPending) {
    const dialog = this.#cartDrawerDialog();
    if (!dialog) return;

    const pendingShell = this.querySelector('[data-cart-drawer-pending-shell]');

    dialog.toggleAttribute('data-cart-drawer-pending', isPending);

    if (isPending) {
      dialog.setAttribute('aria-busy', 'true');
      this.setAttribute('aria-busy', 'true');
      dialog.setAttribute('aria-labelledby', 'cart-drawer-heading-pending');
    } else {
      dialog.removeAttribute('aria-busy');
      this.removeAttribute('aria-busy');
      dialog.setAttribute(
        'aria-labelledby',
        dialog.classList.contains('cart-drawer--empty') ? 'cart-drawer-heading-empty' : 'cart-drawer-heading'
      );
    }

    if (pendingShell instanceof HTMLElement) {
      pendingShell.hidden = !isPending;
      pendingShell.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Updates cart drawer count bubbles while the pending shell is visible.
   * @param {number} itemCount
   */
  #updateCartDrawerVisibleCount(itemCount) {
    const count = this.#normalizeCartItemCount(itemCount, 0);
    const displayCount = count < 100 ? String(count) : '';

    this.querySelectorAll('.cart-drawer__heading .cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', count === 0);

      if (count > 0 && count <= 99) {
        bubble.setAttribute('data-maintain-ratio', '');
      } else {
        bubble.removeAttribute('data-maintain-ratio');
      }
    });

    this.querySelectorAll('.cart-drawer__heading .cart-bubble__text-count').forEach((countElement) => {
      countElement.textContent = displayCount;
      countElement.classList.toggle('hidden', count === 0);
    });

    this.querySelectorAll('[data-cart-drawer-pending-count-sr]').forEach((countElement) => {
      countElement.textContent = String(count);
    });
  }

  /**
   * Reads the drawer's current rendered item count.
   * @returns {number}
   */
  #readCartDrawerItemCount() {
    const countText = this.querySelector('.cart-drawer__heading .cart-bubble__text-count')?.textContent?.trim();
    const count = countText ? parseInt(countText, 10) : 0;

    return Number.isFinite(count) ? count : 0;
  }

  /**
   * Finds this component's containing cart drawer dialog.
   * @returns {HTMLElement | null}
   */
  #cartDrawerDialog() {
    const dialog = this.closest('dialog.cart-drawer__dialog');

    return dialog instanceof HTMLElement ? dialog : null;
  }

  /**
   * Normalizes cart item count event fields.
   * @param {unknown} value
   * @param {number} fallback
   * @returns {number}
   */
  #normalizeCartItemCount(value, fallback) {
    const count = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);

    return Number.isFinite(count) && count >= 0 ? count : fallback;
  }

  /**
   * Morphs cart update HTML while preserving static populated drawer fragments.
   * @param {string} cartItemsHtml - The rendered section HTML.
   * @param {Document} [newSectionHTML] - Parsed rendered section HTML.
   */
  #morphCartItemsSection(cartItemsHtml, newSectionHTML = new DOMParser().parseFromString(cartItemsHtml, 'text/html')) {
    const newCartItemsComponent = this.#findMatchingCartItemsComponent(newSectionHTML);

    if (!newCartItemsComponent || !this.#canMorphPopulatedDrawerFragments(newCartItemsComponent)) {
      morphSection(this.sectionId, cartItemsHtml);
      return;
    }

    const didMorphDrawerFragments = [
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-items__wrapper'),
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-drawer__heading .cart-bubble', { childrenOnly: false }),
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-drawer__summary .cart__summary-totals'),
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-drawer__summary .cart__ctas'),
      this.#morphOptionalFragment(newCartItemsComponent, '.cart-drawer__free-watch-banner', { childrenOnly: false }),
    ].every(Boolean);

    if (!didMorphDrawerFragments) {
      morphSection(this.sectionId, cartItemsHtml);
      return;
    }

    this.updatedCallback();
  }

  /**
   * Morphs the narrow cart drawer fragment rendered by sections/cart-drawer-fragment.liquid.
   * @param {string} fragmentHtml - Rendered drawer fragment HTML.
   * @returns {boolean}
   */
  #morphCartDrawerFragment(fragmentHtml) {
    const fragmentDocument = new DOMParser().parseFromString(fragmentHtml, 'text/html');
    const drawerFragment = fragmentDocument.querySelector('[data-cart-drawer-fragment]');
    const newCartItemsComponent = drawerFragment?.querySelector('cart-items-component');

    if (!(drawerFragment instanceof HTMLElement) || !(newCartItemsComponent instanceof HTMLElement)) return false;

    const currentDialog = this.closest('dialog.cart-drawer__dialog');
    const currentDrawer = this.closest('cart-drawer-component');

    if (!(currentDialog instanceof HTMLElement)) return false;

    newCartItemsComponent.dataset.sectionId = this.sectionId;

    const currentIsEmpty = currentDialog.classList.contains('cart-drawer--empty');
    const newIsEmpty = drawerFragment.dataset.cartDrawerEmpty === 'true';
    const mirrorSource = drawerFragment.dataset.cartDrawerMirrorSource;

    if (currentIsEmpty !== newIsEmpty || newIsEmpty) return false;

    currentDialog.classList.toggle('cart-drawer--empty', newIsEmpty);
    currentDialog.setAttribute('aria-labelledby', newIsEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');

    if (currentDrawer instanceof HTMLElement && mirrorSource) {
      currentDrawer.dataset.cartDrawerMirrorSource = mirrorSource;
    }

    const didMorphDrawerFragments = [
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-items__wrapper'),
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-drawer__heading .cart-bubble', { childrenOnly: false }),
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-drawer__summary .cart__summary-totals'),
      this.#morphRequiredFragment(newCartItemsComponent, '.cart-drawer__summary .cart__ctas'),
      this.#morphOptionalFragment(newCartItemsComponent, '.cart-drawer__free-watch-banner', { childrenOnly: false }),
    ].every(Boolean);

    if (!didMorphDrawerFragments) return false;

    this.updatedCallback();
    return true;
  }

  /**
   * Finds this component's equivalent in rendered section HTML.
   * @param {Document} sectionHTML - Parsed rendered section HTML.
   * @returns {HTMLElement | null}
   */
  #findMatchingCartItemsComponent(sectionHTML) {
    const cartItemsComponents = sectionHTML.querySelectorAll('cart-items-component');

    for (const component of cartItemsComponents) {
      if (component instanceof HTMLElement && component.dataset.sectionId === this.sectionId) {
        return component;
      }
    }

    return null;
  }

  /**
   * Checks whether the update can avoid a full section morph.
   * @param {HTMLElement} newCartItemsComponent - Rendered cart items component.
   * @returns {boolean}
   */
  #canMorphPopulatedDrawerFragments(newCartItemsComponent) {
    const currentDialog = this.closest('dialog.cart-drawer__dialog');
    const newDialog = newCartItemsComponent.closest('dialog.cart-drawer__dialog');

    if (!(currentDialog instanceof HTMLElement) || !(newDialog instanceof HTMLElement)) return false;

    const currentIsEmpty = currentDialog.classList.contains('cart-drawer--empty');
    const newIsEmpty = newDialog.classList.contains('cart-drawer--empty');

    return !currentIsEmpty && !newIsEmpty;
  }

  /**
   * Morphs a required fragment inside this component.
   * @param {HTMLElement} newCartItemsComponent - Rendered cart items component.
   * @param {string} selector - Fragment selector.
   * @param {Object} [options] - Morph options.
   * @returns {boolean}
   */
  #morphRequiredFragment(newCartItemsComponent, selector, options) {
    const currentFragment = this.querySelector(selector);
    const newFragment = newCartItemsComponent.querySelector(selector);

    if (!currentFragment || !newFragment) return false;

    morph(currentFragment, newFragment, options);
    return true;
  }

  /**
   * Morphs an optional fragment when it exists in both current and rendered HTML.
   * @param {HTMLElement} newCartItemsComponent - Rendered cart items component.
   * @param {string} selector - Fragment selector.
   * @param {Object} [options] - Morph options.
   * @returns {boolean}
   */
  #morphOptionalFragment(newCartItemsComponent, selector, options) {
    const currentFragment = this.querySelector(selector);
    const newFragment = newCartItemsComponent.querySelector(selector);

    if (!currentFragment && !newFragment) return true;
    if (!currentFragment || !newFragment) return false;

    morph(currentFragment, newFragment, options);
    return true;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
